import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { forwardRef, useId } from 'react';
import type { ButtonHTMLAttributes, ReactNode, RefObject } from 'react';
import './AppDialog.css';

/** Chakra size scale, plus numeric/string escape hatches for one-off widths. */
export type AppDialogSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '6xl' | 'full';

export interface AppDialogProps {
    isOpen: boolean;
    onClose: () => void;
    /** Dialog body content. */
    children: ReactNode;
    /** Header bar title. The header bar is omitted when absent. */
    title?: ReactNode;
    /** Optional secondary line under the title. */
    subtitle?: ReactNode;
    /** Optional leading icon in the header bar. */
    icon?: ReactNode;
    /** Right-aligned action row rendered in the footer bar. */
    footer?: ReactNode;
    /** Optional left slot in the footer bar (status text, counters, ...). */
    footerLeft?: ReactNode;
    size?: AppDialogSize | number | string;
    /** Dismiss when clicking the backdrop. Default: true. */
    closeOnOverlayClick?: boolean;
    /** Dismiss with the Escape key. Default: true. */
    closeOnEsc?: boolean;
    /** Render the round close button in the header. Default: true. */
    showCloseButton?: boolean;
    /** Internal scroll (dialog keeps a fixed height) vs. whole-dialog scroll. */
    scrollBehavior?: 'inside' | 'outside';
    /** `alert` uses compact HIG alert proportions (ConfirmDialog). */
    variant?: 'standard' | 'alert';
    initialFocusRef?: RefObject<HTMLElement>;
    finalFocusRef?: RefObject<HTMLElement>;
    /** Escape hatch appended to the body class list. */
    bodyClassName?: string;
}

/**
 * The shared dialog shell for the whole application (Apple HIG chrome):
 * dimmed + blurred overlay, themed panel with header/body/footer bars and
 * a round close affordance. Every dialog must render through this shell so
 * theming and behavior stay consistent.
 */
export function AppDialog({
    isOpen,
    onClose,
    children,
    title,
    subtitle,
    icon,
    footer,
    footerLeft,
    size = 'md',
    closeOnOverlayClick = true,
    closeOnEsc = true,
    showCloseButton = true,
    scrollBehavior = 'inside',
    variant = 'standard',
    initialFocusRef,
    finalFocusRef,
    bodyClassName,
}: AppDialogProps) {
    const headingId = useId();
    const hasHeader = Boolean(title || subtitle || icon);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={size as never}
            isCentered
            scrollBehavior={scrollBehavior}
            closeOnOverlayClick={closeOnOverlayClick}
            closeOnEsc={closeOnEsc}
            initialFocusRef={initialFocusRef}
            finalFocusRef={finalFocusRef}
        >
            <ModalOverlay className="app-dialog-overlay" />
            <ModalContent
                className={
                    variant === 'alert'
                        ? 'app-dialog-frame app-dialog-frame--alert suite-ui-dialog'
                        : 'app-dialog-frame suite-ui-dialog'
                }
                aria-labelledby={title ? headingId : undefined}
            >
                {hasHeader && (
                    <div className="app-dialog-header suite-ui-dialog-header">
                        {icon && <span className="app-dialog-icon">{icon}</span>}
                        <div className="app-dialog-heading">
                            {title && (
                                <h2 className="app-dialog-title" id={headingId}>
                                    {title}
                                </h2>
                            )}
                            {subtitle && <p className="app-dialog-subtitle">{subtitle}</p>}
                        </div>
                    </div>
                )}
                {showCloseButton && (
                    <ModalCloseButton className="app-dialog-close-btn" _focusVisible={{ boxShadow: 'none' }} />
                )}
                <div className={bodyClassName ? `app-dialog-body suite-ui-dialog-body ${bodyClassName}` : 'app-dialog-body suite-ui-dialog-body'}>
                    {children}
                </div>
                {footer && (
                    <div className="app-dialog-footer suite-ui-dialog-footer">
                        {footerLeft && <div className="app-dialog-footer-left">{footerLeft}</div>}
                        <div className="app-dialog-footer-actions">{footer}</div>
                    </div>
                )}
            </ModalContent>
        </Modal>
    );
}

export type DialogButtonVariant = 'primary' | 'secondary' | 'destructive' | 'ghost';

export interface DialogButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: DialogButtonVariant;
    /** Chakra-style disabled flag so migrations stay mechanical. */
    isDisabled?: boolean;
}

/**
 * The shared action button used in dialog footers (Apple HIG hierarchy):
 * `primary` = filled accent (the default action), `secondary` = bordered
 * neutral, `destructive` = filled red, `ghost` = borderless text.
 */
export const DialogButton = forwardRef<HTMLButtonElement, DialogButtonProps>(function DialogButton(
    { variant = 'secondary', isDisabled, className, type = 'button', disabled, children, ...rest },
    ref,
) {
    const variantClass = `app-dialog-btn app-dialog-btn--${variant}`;
    return (
        <button
            ref={ref}
            type={type}
            className={className ? `${variantClass} ${className}` : variantClass}
            disabled={disabled ?? isDisabled}
            {...rest}
        >
            {children}
        </button>
    );
});
